import { useCallback, useEffect, useMemo, useState } from 'react';
import type { AuthStatus } from '../../api/MarketplaceAuth';
import {
  getAuthStatus,
  updateIntegration,
} from '../../api/MarketplaceAuth';
import type { MpAuthOption } from '../../api/MpProducts';
import * as MpProductsApi from '../../api/MpProducts';
import type { MpWarehouse, RemoteMpWarehouse } from '../../api/MpWarehouses';
import * as MpWarehousesApi from '../../api/MpWarehouses';
import type { SelfWarehouse } from '../../api/SelfWarehouses';
import * as SelfWarehousesApi from '../../api/SelfWarehouses';

export type CatalogStats = {
  total: number;
  linked: number;
  unlinked: number;
};

export type IntegrationBundle = {
  data: AuthStatus;
  marketplaceAuthId: number | null;
  warehouses: MpWarehouse[];
  remoteWarehouses: RemoteMpWarehouse[];
  selfWarehouses: SelfWarehouse[];
  catalogStats: CatalogStats | null;
  lastCatalogSyncedAt: string | null;
  warehousesUpdatedAt: string | null;
};

export function useIntegrationDetail(marketplace: string) {
  const [data, setData] = useState<AuthStatus | null>(null);
  const [marketplaceAuthId, setMarketplaceAuthId] = useState<number | null>(
    null,
  );
  const [warehouses, setWarehouses] = useState<MpWarehouse[]>([]);
  const [remoteWarehouses, setRemoteWarehouses] = useState<
    RemoteMpWarehouse[]
  >([]);
  const [selfWarehouses, setSelfWarehouses] = useState<SelfWarehouse[]>([]);
  const [catalogStats, setCatalogStats] = useState<CatalogStats | null>(null);
  const [lastCatalogSyncedAt, setLastCatalogSyncedAt] = useState<string | null>(
    null,
  );
  const [warehousesUpdatedAt, setWarehousesUpdatedAt] = useState<string | null>(
    null,
  );
  const [pending, setPending] = useState(true);
  const [sidePending, setSidePending] = useState(true);
  const [error, setError] = useState('');

  const showWarehouses =
    marketplace === 'wildberries' || marketplace === 'ozon';
  const showCatalog =
    marketplace === 'wildberries' || marketplace === 'ozon';

  const reloadCore = useCallback(async () => {
    if (!marketplace) return;
    setPending(true);
    setError('');
    try {
      const [list, auths] = await Promise.all([
        getAuthStatus(),
        MpProductsApi.listAuths().catch(() => [] as MpAuthOption[]),
      ]);
      const found = list.find((el) => el.markerplace === marketplace);
      if (!found) {
        setError('Интеграция не найдена');
        setData(null);
        setMarketplaceAuthId(null);
        return;
      }
      setData(found);
      const auth = auths.find((a) => a.marketplace === marketplace);
      setMarketplaceAuthId(auth?.id ?? null);
    } catch (e: any) {
      setError(e?.message || String(e));
      setData(null);
    } finally {
      setPending(false);
    }
  }, [marketplace]);

  const reloadSide = useCallback(async () => {
    if (!marketplace || !data) return;
    setSidePending(true);
    try {
      const tasks: Promise<void>[] = [];

      if (showWarehouses) {
        tasks.push(
          (async () => {
            const [mp, self] = await Promise.all([
              MpWarehousesApi.list(marketplace),
              SelfWarehousesApi.getAll(),
            ]);
            setWarehouses(mp);
            setSelfWarehouses(self.filter((s) => !s.deleted_at));
            try {
              setRemoteWarehouses(
                await MpWarehousesApi.listRemote(marketplace),
              );
            } catch {
              setRemoteWarehouses([]);
            }
          })(),
        );
      }

      if (showCatalog && marketplaceAuthId != null) {
        tasks.push(
          (async () => {
            const [stats, listRes] = await Promise.all([
              MpProductsApi.stats(marketplaceAuthId),
              MpProductsApi.list({
                marketplace_auth_id: marketplaceAuthId,
                linked: 'all',
                limit: 1,
              }),
            ]);
            setCatalogStats(stats);
            const synced = listRes.items[0]?.synced_at ?? null;
            setLastCatalogSyncedAt(synced);
          })(),
        );
      } else if (!showCatalog) {
        setCatalogStats(null);
        setLastCatalogSyncedAt(null);
      }

      await Promise.all(tasks);
    } finally {
      setSidePending(false);
    }
  }, [marketplace, data, marketplaceAuthId, showWarehouses, showCatalog]);

  useEffect(() => {
    void reloadCore();
  }, [reloadCore]);

  useEffect(() => {
    if (data) void reloadSide();
  }, [data?.markerplace, marketplaceAuthId, reloadSide]);

  const saveAccess = useCallback(
    async (fields: {
      sellerId?: string;
      apiToken?: string;
      cookie?: string;
    }) => {
      await updateIntegration({
        markeplace: marketplace,
        sellerId: fields.sellerId || undefined,
        apiToken: fields.apiToken || undefined,
        cookie: fields.cookie || undefined,
      });
      const list = await getAuthStatus();
      const found = list.find((el) => el.markerplace === marketplace);
      if (found) setData(found);
    },
    [marketplace],
  );

  const refreshWarehouseStatuses = useCallback(async () => {
    const synced = await MpWarehousesApi.sync(marketplace);
    setWarehouses(synced);
    setRemoteWarehouses(await MpWarehousesApi.listRemote(marketplace));
    setWarehousesUpdatedAt(new Date().toISOString());
    return synced;
  }, [marketplace]);

  const checkConnection = useCallback(async () => {
    if (showWarehouses) {
      const remote = await MpWarehousesApi.listRemote(marketplace);
      setRemoteWarehouses(remote);
      return { ok: true, warehouses: remote.length };
    }
    if (showCatalog && marketplaceAuthId != null) {
      const stats = await MpProductsApi.stats(marketplaceAuthId);
      setCatalogStats(stats);
      return { ok: true, products: stats.total };
    }
    return { ok: Boolean(data?.apiToken || data?.cookie) };
  }, [
    showWarehouses,
    showCatalog,
    marketplace,
    marketplaceAuthId,
    data?.apiToken,
    data?.cookie,
  ]);

  const syncCatalog = useCallback(async () => {
    if (marketplaceAuthId == null) {
      throw new Error('Не найден marketplace_auth для синхронизации каталога');
    }
    return MpProductsApi.enqueueSync(marketplaceAuthId);
  }, [marketplaceAuthId]);

  const attention = useMemo(() => {
    const items: { key: string; text: string; tab?: string }[] = [];
    if (!data?.apiToken?.trim() && !data?.cookie?.trim()) {
      items.push({
        key: 'creds',
        text: 'Не заполнены данные доступа',
        tab: 'access',
      });
    }
    const inactive = warehouses.filter((w) => !w.is_active).length;
    if (inactive > 0) {
      items.push({
        key: 'inactive',
        text:
          inactive === 1
            ? 'Один склад отключён'
            : `Отключено складов: ${inactive}`,
        tab: 'warehouses',
      });
    }
    const unmapped = warehouses.filter(
      (w) => !w.self_warehouse_ids?.length,
    ).length;
    if (unmapped > 0) {
      items.push({
        key: 'unmap',
        text:
          unmapped === 1
            ? 'Для одного склада не настроено сопоставление'
            : `Без сопоставления: ${unmapped}`,
        tab: 'warehouses',
      });
    }
    if (showCatalog && catalogStats && catalogStats.unlinked > 0) {
      items.push({
        key: 'unlinked',
        text: `Товаров без связи с 1С: ${catalogStats.unlinked}`,
        tab: 'products',
      });
    }
    return items;
  }, [data, warehouses, catalogStats, showCatalog]);

  return {
    pending,
    sidePending,
    error,
    data,
    marketplaceAuthId,
    warehouses,
    setWarehouses,
    remoteWarehouses,
    selfWarehouses,
    catalogStats,
    lastCatalogSyncedAt,
    warehousesUpdatedAt,
    showWarehouses,
    showCatalog,
    attention,
    reloadCore,
    reloadSide,
    saveAccess,
    refreshWarehouseStatuses,
    checkConnection,
    syncCatalog,
  };
}
